import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { truncate } from 'src/utils/string';
import { formatDateToNOString } from 'src/utils/FormatValue';
import OneWayInOpportunityStatusTag from '../components/OneWayInOpportunityStatusTag';
import { ONE_WAY_IN_DETAILS } from '../../opportunity-details/routes';
import useGeoNorge from '../../hooks/use-geonorge';
import { translateStatusTag } from '../helpers/statusTags';
import { FaIcon, FaIcons } from '@innovationnorway/component-library';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus, SimpleServiceDto } from 'src/api/v2';
import { useUsers } from 'src/features/users';
import { Button } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import { SERVICES } from 'src/features/services';

const columnHelper = createColumnHelper<OneWayInOpportunitySimpleV2Dto>();

export const DeliveredServicesList = ({ services }: { services: SimpleServiceDto[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t: tCommon } = useTranslation('common');

  const displayServices = isExpanded ? services : services.slice(0, 3);
  const hasMoreServices = services.length > 3;

  return (
    <div className="display--flex align-items--center flex-wrap--wrap gap--3-halfs">
      {displayServices.map((service) => {
        if (!service?.name || !service.id) return null;
        return (
          <Link key={service.id} to={`${SERVICES.BASE}/${service.id}`}>
            {service.name}
          </Link>
        );
      })}
      {hasMoreServices && !isExpanded && (
        <Button variant="tertiary" onClick={() => setIsExpanded(true)} type="button">
          {tCommon('seeMore')}
        </Button>
      )}
      {hasMoreServices && isExpanded && (
        <Button variant="tertiary" onClick={() => setIsExpanded(false)} type="button">
          {tCommon('seeLess')}
        </Button>
      )}
    </div>
  );
};

export const useOpportunityTableColumns = () => {
  const { getMunicipalityNameByNumber } = useGeoNorge();
  const { users } = useUsers();

  return useMemo(() => {
    return [
      columnHelper.accessor('title', {
        id: 'title',
        minSize: 400,
        cell: ({
          getValue,
          row: {
            original: { oneWayInId },
          },
        }) => {
          return (
            <Link aria-label={getValue()} to={ONE_WAY_IN_DETAILS.BASE(oneWayInId)}>
              {truncate(getValue(), 60)}
            </Link>
          );
        },
      }),
      columnHelper.accessor((row) => translateStatusTag(row.status?.status), {
        id: 'status',
        maxSize: 100,
        sortingFn: statusSort,
        cell: (info) => <OneWayInOpportunityStatusTag status={info.row.original.status?.status} />,
      }),
      columnHelper.accessor((row) => (row.submittedOn ? new Date(row.submittedOn) : null), {
        id: 'submittedDate',
        minSize: 200,
        cell: (info) => {
          const infoValue = info ? info.getValue() : null;
          return infoValue ? formatDateToNOString(infoValue, false, '{date}.{month}.{year}', true) : '';
        },

        sortingFn: 'datetime',
      }),
      columnHelper.accessor((row) => (row.lastChangedOn ? new Date(row.lastChangedOn) : null), {
        id: 'lastChangedDate',
        maxSize: 150,
        cell: (info) => {
          const infoValue = info ? info.getValue() : null;
          return infoValue ? formatDateToNOString(infoValue, false, '{date}.{month}.{year}', true) : '';
        },
        sortingFn: 'datetime',
      }),
      columnHelper.accessor((row) => getMunicipalityNameByNumber(row.municipality), {
        id: 'municipality',
        filterFn: 'includesString',
        maxSize: 130,
      }),
      columnHelper.accessor((row) => row.customerName || '', {
        id: 'customerName',
        minSize: 200,
      }),
      columnHelper.accessor((row) => row.organizationName || '', {
        id: 'organizationName',
        cell: (info) => {
          const organizationName = info.getValue();
          return organizationName ? (
            <>
              <FaIcon name={FaIcons.BuildingLight} /> {organizationName}
            </>
          ) : (
            ''
          );
        },
      }),
      columnHelper.accessor(
        (row) => {
          const advisor = users.find((user) => user.id === row.assignedTo);
          const advisorName = advisor ? `${advisor.firstName} ${advisor.lastName}` : '';
          return advisorName ?? row.assignedTo;
        },
        {
          id: 'advisor',
        },
      ),
      columnHelper.accessor(
        (row) =>
          row.deliveredServices !== undefined && row.deliveredServices.length > 0
            ? row.deliveredServices
                ?.filter((service) => service.name !== undefined && service.id !== undefined)
                .map((service) => service.name)
                .join('; ')
            : '',
        {
          id: 'deliveredServices',
          enableSorting: false,
          cell: (info) => {
            const services = info.row.original.deliveredServices;

            if (!services || services.length === 0) {
              return undefined;
            }

            return <DeliveredServicesList services={services} />;
          },
        },
      ),
    ];
  }, [getMunicipalityNameByNumber, users]);
};

const statusSort = (rowA: Row<OneWayInOpportunitySimpleV2Dto>, rowB: Row<OneWayInOpportunitySimpleV2Dto>) => {
  const getSortValue = (row: Row<OneWayInOpportunitySimpleV2Dto>) => {
    switch (row.original.status?.status) {
      case OneWayInStatus.New:
        return 999;
      case OneWayInStatus.Received:
        return 999;
      case OneWayInStatus.Active:
        return 30;
      case OneWayInStatus.Opened:
        return 30;
      case OneWayInStatus.ReOpened:
        return 30;
      case OneWayInStatus.Shared:
        return 30;
      case OneWayInStatus.FollowUpLater:
        return 20;
      case OneWayInStatus.Closed:
        return 10;
      case OneWayInStatus.Forwarded:
        return 10;
      default:
        return 0;
    }
  };
  return getSortValue(rowA) - getSortValue(rowB);
};
